import React from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
	Badge,
	Space,
	Switch,
	Typography,
} from 'antd';
import { Moment } from 'moment';
import {
	BankAccountName,
	Currencies,
	DATE_AND_TIME,
	ShowNothingInvoiceCompanyName,
	VesselOwnershipTypeLabels,
	VesselOwnershipTypes,
	VesselTypeLabels,
	VesselTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatNumber } from '@shared/utils/formatNumber';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { GetUserDataResponse } from '@api/features/user/getUserData';
import type { BankAccountProps } from '@api/models/bank-account';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import { formatDate } from '@client/utils/formatDate';
import { renderDate } from '@client/utils/table';
import TooltipIcon from '@client/components/TooltipIcon';
import NumericInput from '@client/components/NumericInput';
import CountryFlag from '@client/components/CountryFlag';
import FlagSelector from '@client/components/FlagSelector';
import EmptyText from '@client/components/EmptyText';
import Select from '@client/components/Select';
import { countries } from '@client/utils/countries';
import { EditableColumn } from '@client/components/EditableTable';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';
import { OwnerPeriod } from '../tabs/VesselDetailsTab';
import styles from '../VesselDetailsScreen.module.css';
import getSizeOptions from './getVesselSizeOptions';

const getCustomNumberRender = ({
	value,
}: {
	value: number;
}) => (
	value != null ?
		formatNumber(value, { separateThousands: true }) :
		(<EmptyText />)
);

export const getCommsItems = (
	vessel: GetVesselDetailsResponse | undefined,
) => {
	if (vessel == null) {
		return [];
	}

	const {
		commsCallSign,
		commsInmarsatNumber,
		commsInmarsatFax,
		commsInmarsatEmail,
	} = vessel;

	return [
		{
			key: 'commsCallSign',
			label: 'Call Sign',
			type: 'text' as const,
			value: commsCallSign,
			editable: true,
		},
		{
			key: 'commsInmarsatNumber',
			label: 'Inmarsat Telephone Number',
			value: commsInmarsatNumber,
			type: 'text' as const,
			editable: true,
		},
		{
			key: 'commsInmarsatFax',
			label: 'Inmarsat Fax Number',
			value: commsInmarsatFax,
			type: 'text' as const,
			editable: true,
		},
		{
			key: 'commsInmarsatEmail',
			label: 'Inmarsat Email',
			value: commsInmarsatEmail,
			type: 'text' as const,
			editable: true,
		},
	];
};

export const getBankingItems = (
	account: BankAccountProps,
	allowCurrencyChange: boolean,
	vesselOwner: boolean,
	showInvoiceCompanyName: boolean,
	fixingCompanies?: Array<FixingCompanyProps>,
) => {
	const {
		accountNo,
		iban,
		bankName,
		swift,
		currency,
		name,
		businessUnit,
		companyInvoiceName,
		fixingCompanyId,
	} = account;

	return [
		{
			key: 'name',
			label: (
				<span>
					Beneficiary Name
				</span>
			),
			value: name,
			editable: true,
		},
		...(fixingCompanies == null ? [] : [{
			key: 'fixingCompanyId',
			label: 'Fixing Company',
			value: fixingCompanyId,
			type: 'select',
			options: getFixingCompanyOptions(fixingCompanies, fixingCompanyId),
			editable: false,
		}]),
		...(vesselOwner ? [] : [{
			key: 'businessUnit',
			label: (
				<span>
					Business Unit
					<Badge
						dot
						color="red"
						className={styles.bankAccountBadge}
					/>
				</span>
			),
			value: businessUnit,
			editable: true,
		}]),
		{
			key: 'accountNo',
			label: 'Account No.',
			value: accountNo,
			editable: true,
		},
		{
			key: 'iban',
			label: 'IBAN',
			value: iban,
			editable: true,
		},
		{
			key: 'bankName',
			label: 'Beneficiary Bank',
			value: bankName,
			editable: true,
		},
		{
			key: 'swift',
			label: 'SWIFT / BIC',
			value: swift,
			editable: true,
		},
		{
			key: 'currency',
			label: (
				<span>
					Currency
					<TooltipIcon>
						Currency can only be changed if the vessel is not fixed to a voyage
					</TooltipIcon>
				</span>
			),
			value: currency,
			type: 'select',
			options: Object.values(Currencies).map((t) => ({
				label: t,
				value: t,
			})),
			inputProps: {
				showSearch: true,
			},
			editable: allowCurrencyChange,
		},
		...(showInvoiceCompanyName ?
			[
				{
					key: 'companyInvoiceName',
					label: 'Company Invoice Name',
					value: companyInvoiceName,
					editable: true,
				},
			] :
			[]
		),
	];
};

export const getGeneralItems = (
	vessel: GetVesselDetailsResponse,
	userData: GetUserDataResponse,
	selectedVesselType?: Values<typeof VesselTypes> | null,
) => {
	const {
		organizationName,
		useVesselCompanyOnInvoice: orgUseVesselCompanyOnInvoice,
	} = userData;
	const {
		vatId,
		name,
		codename,
		ownershipType,
		companyName,
		useVesselCompanyOnInvoice,
		invoiceCompanyNameToUse,
		yard,
		yearBuilt,
		flag,
		portOfRegistry,
		imo,
		classificationId,
		vesselClass,
		isIceClass,
		lastSpecialSurvey,
		lastDryDocking,
		nextSpecialSurvey,
		nextDryDocking,
		ownersPIClub,
		mastersName,
		LOA,
		breadth,
		summerDWT,
		ladenDraft,
		ballastDraft,
		lightship,
		grossTonnage,
		netTonnage,
		suezGRT,
		suezNRT,
		panamaNRT,
		grainCapacity,
		TPC,
		summerDraft,
		constants,
		baleCapacity,
		cranes,
		grabs,
		conditionBoundary,
		createdAt,
		updatedAt,
		type,
		size,
		technicalOperator,
		commercialOperator,
		disponentOwner,
		lastDryDockingLocation,
		lastAnnualSurvey,
		lengthBetweenPerpendiculars,
		fittedForPanamaCanal,
		fittedForSuezCanal,
		fittedForStLawrenceSeaway,
		permittedCargos,
		totalCargoTankCapacities98,
		totalCargoTankCapacities100,
		maxNumberOfLoadedGrades,
		mouldedDepth,
		laneMetersTrucks,
		laneMetersCars,
		reeferPlugs,
		reeferPlugVoltageHz,
		crewCabins,
		maxPassengerCapacity,
		crewBunks,
		passengerCabins,
		passengerBunks,
		shipManager,
		registeredOwner,
		previousNames,
	} = vessel;

	let calculatedSize = size;

	if (selectedVesselType != null && selectedVesselType !== type) {
		calculatedSize = null;
	}

	const getCompanyName = (value: boolean | undefined | string) => {
		if (value === true) {
			return companyName || 'Vessel company name';
		}

		if (value === false) {
			return organizationName ?? '';
		}

		if (typeof value === 'string') {
			return value;
		}

		return orgUseVesselCompanyOnInvoice ?
			`${companyName || 'Vessel company name'}` :
			`${organizationName}`;
	};

	const keyDetails = [
		{
			key: 'imo',
			label: 'IMO',
			value: Number(imo),
			editable: true,
			type: 'number' as const,
			renderInput: (
				{ value, onChange }: { value?: number; onChange: (values: number | null) => void },
			) => (
				<NumericInput
					addonBefore="IMO"
					separateThousands={false}
					allowNegative={false}
					value={value != null ? Number(value) : null}
					onChange={onChange}
				/>
			),
			render: ({ value }: { value?: any }) => (value != null ? (
				<>
					IMO
					{' '}
					{value}
				</>
			) : (
				<span>
					<EmptyText />
					<TooltipIcon danger Icon={ExclamationCircleOutlined} placement="bottom">
						An IMO number is required to enable AIS functionality
					</TooltipIcon>
				</span>
			)),
		},
		{
			key: 'name',
			label: 'Vessel name',
			value: name,
			editable: true,
			type: 'text' as const,
		},
		{
			key: 'type',
			label: 'Vessel type',
			value: type,
			editable: true,
			type: 'select' as const,
			options: Object.keys(VesselTypes).map((t) => ({
				label: VesselTypeLabels[t as keyof typeof VesselTypeLabels],
				value: t,
			})),
		},
		{
			key: 'size',
			label: 'Vessel size',
			value: calculatedSize,
			editable: true,
			type: 'select' as const,
			options: getSizeOptions(selectedVesselType ?? type),
		},
		{
			key: 'ownershipType',
			label: 'Ownership Type',
			value: ownershipType,
			editable: true,
			type: 'select' as const,
			options: Object.values(VesselOwnershipTypes).map((t) => ({
				label: VesselOwnershipTypeLabels[t as keyof typeof VesselOwnershipTypeLabels],
				value: t,
			})),
			render: ({ value }: { value?: Values<typeof VesselOwnershipTypes> | null }) => (
				value != null ? VesselOwnershipTypeLabels[value] : <EmptyText />
			),
			renderInput: (
				{ value, onChange }: {
					value?: Values<typeof VesselOwnershipTypes> | null;
					onChange: (values: Values<typeof VesselOwnershipTypes> | null) => void;
				},
			) => (
				<Select<Values<typeof VesselOwnershipTypes>>
					fullWidth
					value={value ?? null}
					onChange={onChange}
					options={Object.values(VesselOwnershipTypes).map((t) => ({
						label: VesselOwnershipTypeLabels[t as keyof typeof VesselOwnershipTypeLabels],
						value: t,
					}))}
				/>
			),
		},
		...(vessel.ownershipType !== VesselOwnershipTypes.MARKET ?
			[
				{
					key: 'codename',
					label: 'Vessel codename',
					value: codename,
					editable: true,
					type: 'text' as const,
				},
				{
					key: 'companyName',
					label: 'Company name',
					value: companyName,
					editable: true,
					type: 'text' as const,
				},
			] :
			[]
		),
	];

	const vesselParticulars = [
		{
			key: 'vatId',
			label: 'VAT number',
			value: vatId,
			editable: true,
			type: 'text' as const,
		},
		{
			key: 'invoiceCompanyNameToUse',
			label: 'Invoice company name',
			type: 'select' as const,
			render: () => {
				return invoiceCompanyNameToUse != null ? (
					<span>
						<Typography.Text>
							{invoiceCompanyNameToUse}
						</Typography.Text>
					</span>
				) : <></>;
			},
			renderInput: ({ onChange }: { value?: string; onChange: (values: any) => void }) => {
				const baseOptions = [
					{ label: getCompanyName(true), value: getCompanyName(true) },
					{ label: getCompanyName(false), value: getCompanyName(false) },
					{ label: BankAccountName, value: BankAccountName },
					{ label: ShowNothingInvoiceCompanyName, value: ShowNothingInvoiceCompanyName },
				];

				return (
					<Select<string>
						fullWidth
						onChange={onChange}
						value={invoiceCompanyNameToUse ?? getCompanyName(undefined)}
						options={baseOptions}
					/>
				);
			},
			value: useVesselCompanyOnInvoice as boolean,
			editable: true,
		},
		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'technicalOperator',
					label: 'Technical operator',
					value: technicalOperator,
					editable: true,
				},
				{
					key: 'commercialOperator',
					label: 'Commercial operator ',
					value: commercialOperator,
					editable: true,
				},
				{
					key: 'disponentOwner',
					label: 'Disponent owner',
					value: disponentOwner,
					editable: true,
				},
			] :
			[]
		),
		{
			key: 'conditionBoundary',
			label: (
				<Space>
					<div>Draft threshold (Ballast/Laden)</div>
					<TooltipIcon>
						Enter the draft threshold that determines if the vessel is considered
						ballast or laden based on whether the actual draft is below or above this value
					</TooltipIcon>
				</Space>
			),
			value: conditionBoundary,
			type: 'number',
			editable: true,
		},
		{
			key: 'shipManager',
			label: 'Ship Manager',
			value: shipManager,
			editable: true,
		},
		{
			key: 'registeredOwner',
			label: 'Registered Owner',
			value: registeredOwner,
			editable: true,
		},
		{
			key: 'previousNames',
			label: 'Previous Names',
			value: previousNames,
			editable: true,
		},
		{
			key: 'yard',
			label: 'Shipyard',
			value: yard,
			editable: true,
		},
		{
			key: 'yearBuilt',
			label: 'Year built',
			value: yearBuilt,
			editable: true,
		},
		{
			key: 'flag',
			label: 'Vessel flag',
			render: ({ value }: { value?: any }) => (
				value != null && (
					<CountryFlag
						countryCode={vessel.flag == null ? undefined : vessel.flag}
						showCountryName
					/>
				)
			),
			renderInput: ({ value, onChange }: {
				value: Values<typeof countries>;
				onChange: (values: any) => void;
			}) => (
				<FlagSelector
					countryCode={value}
					onChange={onChange}
				/>
			),
			value: flag,
			editable: true,
		},
		{
			key: 'portOfRegistry',
			label: 'Port of registry',
			value: portOfRegistry,
			editable: true,
		},
		{
			key: 'vesselClass',
			label: 'Vessel class',
			value: vesselClass,
			editable: true,
		},
		{
			key: 'isIceClass',
			label: 'Ice class',
			value: isIceClass,
			editable: true,
			type: 'switch' as const,
			render: () => (isIceClass ? 'Yes' : 'No'),
		},
		{
			key: 'classificationId',
			label: 'Classification id',
			value: classificationId,
			editable: true,
		},
		{
			key: 'lastSpecialSurvey',
			label: 'Last special survey',
			value: lastSpecialSurvey ? formatDate(lastSpecialSurvey) : null,
			editable: true,
			type: 'date',
		},
		{
			key: 'lastDryDocking',
			label: 'Last dry docking',
			value: lastDryDocking ? formatDate(lastDryDocking) : null,
			editable: true,
			type: 'date',
		},
		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'lastDryDockingLocation',
					label: 'Last dry docking location',
					value: lastDryDockingLocation,
					editable: true,
				},
			] :
			[]
		),
		{
			key: 'nextSpecialSurvey',
			label: 'Next special survey',
			value: nextSpecialSurvey ? formatDate(nextSpecialSurvey) : null,
			editable: true,
			type: 'date',
		},
		{
			key: 'nextDryDocking',
			label: 'Next dry docking',
			value: nextDryDocking ? formatDate(nextDryDocking) : null,
			editable: true,
			type: 'date',
		},
		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'lastAnnualSurvey',
					label: 'Last annual survey',
					value: lastAnnualSurvey,
					editable: true,
					type: 'date',
				},
			] :
			[]
		),
		{
			key: 'ownersPIClub',
			label: 'Owners PI club',
			value: ownersPIClub,
			editable: true,
		},
		{
			key: 'mastersName',
			label: 'Masters name',
			value: mastersName,
			editable: true,
		},
		{
			key: 'LOA',
			label: 'LOA',
			value: LOA,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'lengthBetweenPerpendiculars',
					label: 'Length between perpendiculars',
					value: lengthBetweenPerpendiculars,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
			] :
			[]
		),
		{
			key: 'breadth',
			label: 'Breadth',
			value: breadth,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'mouldedDepth',
					label: 'Moulded depth',
					value: mouldedDepth,
					type: 'number',
					render: getCustomNumberRender,
					editable: true,
				},
			] :
			[]
		),
		{
			key: 'summerDWT',
			label: 'Summer DWT',
			value: summerDWT,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'ladenDraft',
			label: 'Default Laden Draft',
			value: ladenDraft,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'ballastDraft',
			label: 'Default Ballast Draft',
			value: ballastDraft,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'summerDraft',
			label: 'Summer Draft',
			value: summerDraft,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'constants',
			label: 'Constants',
			value: constants,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'lightship',
			label: 'Lightship',
			value: lightship,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'TPC',
			label: 'TPC',
			value: TPC,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'grossTonnage',
			label: 'Gross Tonnage',
			value: grossTonnage,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'netTonnage',
			label: 'Net Tonnage',
			value: netTonnage,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'suezGRT',
			label: 'Suez GRT',
			value: suezGRT,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'suezNRT',
			label: 'Suez NRT',
			value: suezNRT,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		{
			key: 'panamaNRT',
			label: 'Panama NRT',
			value: panamaNRT,
			editable: true,
			type: 'number',
			render: getCustomNumberRender,
		},
		...(vessel.type !== VesselTypes.LPG ?
			[
				{
					key: 'grainCapacity',
					label: 'Grain Capacity',
					value: grainCapacity,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'baleCapacity',
					label: 'Bale Capacity',
					value: baleCapacity,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'cranes',
					label: 'Cranes',
					value: cranes,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'grabs',
					label: 'Grabs',
					value: grabs,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
			] :
			[]
		),

		...(vessel.type === VesselTypes.ROPAX ?
			[
				{
					key: 'laneMetersTrucks',
					label: 'Lane meters (trucks)',
					value: laneMetersTrucks,
					editable: true,
				},
				{
					key: 'laneMetersCars',
					label: 'Lane meters (cars)',
					value: laneMetersCars,
					editable: true,
				},
				{
					key: 'reeferPlugs',
					label: 'Reefer plugs',
					value: reeferPlugs,
					editable: true,
				},
				{
					key: 'reeferPlugVoltageHz',
					label: 'Reefer plug voltage (Hz)',
					value: reeferPlugVoltageHz,
					editable: true,
				},
				{
					key: 'crewCabins',
					label: 'Crew cabins',
					value: crewCabins,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'crewBunks',
					label: 'Crew bunks',
					value: crewBunks,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'passengerCabins',
					label: 'Passenger cabins',
					value: passengerCabins,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'passengerBunks',
					label: 'Passenger bunks',
					value: passengerBunks,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'maxPassengerCapacity',
					label: 'Max passenger capacity',
					value: maxPassengerCapacity,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},

			] :
			[]
		),

		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'permittedCargos',
					label: 'Permitted cargos',
					value: permittedCargos,
					editable: true,
				},
				{
					key: 'totalCargoTankCapacities98',
					label: 'Total cargo tank capacities 98%',
					value: totalCargoTankCapacities98,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'totalCargoTankCapacities100',
					label: 'Total cargo tank capacities 100%',
					value: totalCargoTankCapacities100,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
				{
					key: 'maxNumberOfLoadedGrades',
					label: 'Maximum number of loaded grades',
					value: maxNumberOfLoadedGrades,
					editable: true,
					type: 'number',
					render: getCustomNumberRender,
				},
			] :
			[]
		),

		{
			key: 'createdAt',
			label: 'Creation date',
			value: formatDate(createdAt, DATE_AND_TIME),
		},
		{
			key: 'updatedAt',
			label: 'Last updated',
			value: formatDate(updatedAt, DATE_AND_TIME),
		},
		...(vessel.type === VesselTypes.LPG ?
			[
				{
					key: 'fittedForPanamaCanal',
					label: 'Fitted for Panama Canal',
					value: fittedForPanamaCanal === true ? 'true' : 'false',
					editable: true,
					type: 'switch',
					renderInput: ({ onChange }: { onChange: () => void }) => {
						return (
							<Switch
								defaultValue={fittedForPanamaCanal || false}
								onChange={onChange}
							/>
						);
					},
				},
				{
					key: 'fittedForSuezCanal',
					label: 'Fitted for Suez Canal',
					value: fittedForSuezCanal === true ? 'true' : 'false',
					editable: true,
					type: 'switch',
					renderInput: ({ onChange }: { onChange: () => void }) => {
						return (
							<Switch
								defaultValue={fittedForSuezCanal || false}
								onChange={onChange}
							/>
						);
					},
				},
				{
					key: 'fittedForStLawrenceSeaway',
					label: 'Fitted for St Lawrence Seaway',
					value: fittedForStLawrenceSeaway === true ? 'true' : 'false',
					editable: true,
					type: 'switch',
					renderInput: ({ onChange }: { onChange: () => void }) => {
						return (
							<Switch
								defaultValue={fittedForStLawrenceSeaway || false}
								onChange={onChange}
							/>
						);
					},
				},
			] :
			[]
		),
	];

	return {
		keyDetails,
		vesselParticulars,
	};
};

export const ownedPeriodsColumns: Array<EditableColumn<
	OwnerPeriod,
	'id'
>> = [
	{
		dataIndex: 'fromDate',
		title: 'Owned From',
		render: renderDate(DATE_AND_TIME),
		editable: true,
		editingProps: {
			type: 'date',
			inputProps: {
				time: true,
			},
		},
	},
	{
		dataIndex: 'toDate',
		title: 'Owned Until',
		render: (v: Moment) => (v == null ? (
			<em>Ongoing</em>
		) : formatDate(v, DATE_AND_TIME)),
		editable: true,
		editingProps: {
			type: 'date',
			required: false,
			inputProps: {
				time: true,
				placeholder: 'Until (optional)',
			},
		},
	},
];
